import React, { Component } from 'react';
import { array, bool, func, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';
import { GoogleAnalyticsHandler } from '../../analytics/handlers';
import { pushEcommerceEvent, pushEvent, listingsToItemList } from '../../analytics/dataLayer';
import { trackSiteSearch } from '../../analytics/matomo';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import { isAnyFilterActive, isMainSearchTypeKeywords, isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { TypeformEmbed } from '../../components/TypeformEmbed/TypeformEmbed';
import { MARKET_PREFERENCE_KEY, DEFAULT_MARKET } from '../../util/markets';

import { H3, H5, Page, Modal } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { languagesHelper as language } from '../../helpers/languages';
import CountryFilterBanner from '../../components/CountryFilterBanner/CountryFilterBanner';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import MainPanelHeaderWithFilters from './MainPanelHeaderWithFilters/MainPanelHeaderWithFilters';
import { shopFilters } from '../../config/configLayout';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import { googleAnalyticsEnabled } from '../../config/configAnalytics';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const REACT_APP_COUNTRY_FILTER_BANNER_ENABLED =
  process.env.REACT_APP_COUNTRY_FILTER_BANNER_ENABLED === 'true';

// Describes a shop query for Matomo Site Search. Free-text keywords are tracked
// as-is; otherwise the active demand filters (category, condition, price, ...)
// form the keyword, so zero-result filter combinations surface in Matomo's
// "No Result Keywords" report. pub_market is a locale preference, not demand.
const isDemandFilter = ([key, value]) =>
  value != null && key !== 'pub_market' && (key === 'price' || key.startsWith('pub_'));

const toSiteSearch = searchParams => {
  const { keywords, ...rest } = searchParams || {};
  if (keywords) {
    return { keyword: String(keywords), category: 'keywords' };
  }
  const filters = Object.entries(rest)
    .filter(isDemandFilter)
    .map(([key, value]) => `${key.replace('pub_', '')}=${value}`)
    .sort();
  return filters.length > 0 ? { keyword: filters.join(' '), category: 'filters' } : null;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      feedbackModalOpen: false,
      hasFeedbackModalBeenOpened: false,
      visitCountReached: false,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.openModalWithFeedbackForm = this.openModalWithFeedbackForm.bind(this);
    this.closeModalWithFeedbackForm = this.closeModalWithFeedbackForm.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.handleFilterChange = this.getHandleChangedValueFn(true);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Create an instance of GoogleAnalyticsHandler
    this.analyticsHandler = new GoogleAnalyticsHandler();
  }

  componentDidMount() {
    // Send the "ShopHomeView" event when the component is mounted
    if (googleAnalyticsEnabled == true) {
      this.analyticsHandler.trackCustomEvent('ShopHomeView');
    }
    // Matomo
    if (typeof _paq !== 'undefined') {
      _paq.push(['trackEvent', 'Shopping', 'ShopHomeView']);
    }

    this.incrementVisitCount();

    // GA4 view_item_list + search (listings may already be present from SSR)
    this.trackSearchResults();

    if (REACT_APP_COUNTRY_FILTER_BANNER_ENABLED) {
      // Only process market filter initialization when country filter feature is enabled
      const { searchParams = {}, location } = this.props;

      // Case 1: We have pub_market in searchParams but not in URL
      // This indicates the filter was applied during initial state load but URL wasn't updated
      if (searchParams && searchParams.pub_market) {
        this.handleFilterChange({ pub_market: searchParams.pub_market });
      }
      // Case 2: No pub_market in either URL or searchParams
      // Fall back to localStorage value or use the default market value
      else if (!searchParams || !searchParams.pub_market) {
        const marketValues = localStorage.getItem(MARKET_PREFERENCE_KEY) || DEFAULT_MARKET;
        this.handleFilterChange({ pub_market: marketValues });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const currentLocale = language.get();

    // This handles the case when clicking the Shop link while already on the SearchPage
    if (prevProps.location.search !== this.props.location.search) {
      const urlParams = new URLSearchParams(this.props.location.search);
      const hasRefreshParam = urlParams.has('refresh');

      if (hasRefreshParam) {
        if (REACT_APP_COUNTRY_FILTER_BANNER_ENABLED) {
          const marketValues = localStorage.getItem(MARKET_PREFERENCE_KEY) || DEFAULT_MARKET;
          this.resetAllExceptMarket(marketValues);
        } else {
          this.resetAll();
        }
      }
    }

    if (
      this.state.visitCountReached &&
      !this.state.hasFeedbackModalBeenOpened &&
      process.env[`REACT_APP_TYPEFORM_ID_BUYER_${currentLocale.toUpperCase()}`]
    ) {
      this.openModalWithFeedbackForm();
      this.setState({ hasFeedbackModalBeenOpened: true });
    }

    // GA4 view_item_list + search when the result set or query changes
    this.trackSearchResults();
  }

  // Pushes GA4 view_item_list when the result set changes, a standard `search`
  // event when the query term changes, and a Matomo site search (with result
  // count) when a new shop query has finished loading. All are deduped against
  // the last tracked value so they don't re-fire on unrelated re-renders.
  trackSearchResults() {
    // Runs in lifecycle methods, so it must never throw and break the page.
    try {
      const {
        listings = [],
        location = {},
        pagination,
        searchInProgress,
        searchListingsError,
        searchParams,
      } = this.props;

      const ids = listings.map(l => l?.id?.uuid).filter(Boolean);
      const signature = ids.join(',');
      if (ids.length && signature !== this._lastListSignature) {
        this._lastListSignature = signature;
        pushEcommerceEvent(
          'view_item_list',
          listingsToItemList(listings, {
            itemListId: 'search_results',
            itemListName: 'Search results',
          })
        );
      }

      const urlParams = parse(location.search || '');
      const { keywords } = urlParams;
      if (keywords && keywords !== this._lastSearchTerm) {
        this._lastSearchTerm = keywords;
        pushEvent('search', { search_term: keywords });
      }

      // Store searchParams and pagination describe the same completed request, so
      // the result count belongs to the tracked query. Comparing against the URL
      // skips a stale store (search still pending, or remount after navigating back).
      const siteSearch = toSiteSearch(searchParams);
      const resultCount =
        pagination?.totalItems ?? (pagination?.paginationUnsupported ? listings.length : null);
      const resultsLoaded =
        !searchInProgress &&
        !searchListingsError &&
        resultCount != null &&
        siteSearch?.keyword === toSiteSearch(urlParams)?.keyword;
      if (siteSearch && resultsLoaded && siteSearch.keyword !== this._lastSiteSearch) {
        this._lastSiteSearch = siteSearch.keyword;
        trackSiteSearch(siteSearch.keyword, siteSearch.category, resultCount);
      }
    } catch (e) {
      // Analytics is best-effort; never let it affect search rendering.
    }
  }

  incrementVisitCount() {
    const currentLocale = language.get();
    if (process.env[`REACT_APP_TYPEFORM_ID_BUYER_${currentLocale.toUpperCase()}`]) {
      const newCount = parseInt(localStorage.getItem('shopVisits') || '0', 10) + 1;
      localStorage.setItem('shopVisits', newCount);
      if (newCount === 4) {
        this.setState({ visitCountReached: true });
      }
    }
  }

  openModalWithFeedbackForm() {
    if (!this.state.feedbackModalOpen) {
      this.setState({ feedbackModalOpen: true });
    }
  }

  closeModalWithFeedbackForm() {
    this.setState({ feedbackModalOpen: false });
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  resetAll(e) {
    const { history, routeConfiguration } = this.props;

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = {};
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  resetAllExceptMarket(marketValue) {
    const { history, routeConfiguration } = this.props;

    // Reset state but keep track of what we want to preserve
    this.setState({ currentQueryParams: {} }, () => {
      // After state is reset, push new URL with only market parameter
      const queryParams = marketValue ? { pub_market: marketValue } : {};
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      conversionRates,
    } = this.props;

    const { feedbackModalOpen } = this.state;

    let { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;

    const [customPrimaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );

    const availableFilters = [...customPrimaryFilters, ...defaultFilters];

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
        className={css.pageRoot}
      >
        {feedbackModalOpen && (
          <Modal
            id="buyer-feedback"
            isOpen={feedbackModalOpen}
            onClose={this.closeModalWithFeedbackForm}
            usePortal
            onManageDisableScrolling={this.props.onManageDisableScrolling}
          >
            <TypeformEmbed isVisible={true} formType="BUYER" />
          </Modal>
        )}
        <TopbarContainer
          className={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
        />

        <div className={css.bannerContainer}>
          {REACT_APP_COUNTRY_FILTER_BANNER_ENABLED && (
            <CountryFilterBanner
              onActionClick={market => {
                localStorage.setItem(MARKET_PREFERENCE_KEY, market);
                this.handleFilterChange({ pub_market: market });
              }}
            />
          )}
        </div>

        <div className={css.layoutWrapperContainer}>
          {shopFilters.filterType === 'sidebar' && (
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              <div className={css.filterColumnContent}>
                {availableFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersMobile.${config.key}`}
                      idPrefix="SearchFiltersMobile"
                      className={css.filter}
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={urlQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                      conversionRates={conversionRates}
                    />
                  );
                })}
                <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </button>
              </div>
            </aside>
          )}

          <div
            className={
              shopFilters.filterType === 'header'
                ? css.layoutWrapperMainHeaderFilters
                : css.layoutWrapperMain
            }
            role="main"
          >
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
              >
                {availableFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersMobile.${config.key}`}
                      idPrefix="SearchFiltersMobile"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      conversionRates={conversionRates}
                    />
                  );
                })}
              </SearchFiltersMobile>
              {shopFilters.filterType === 'sidebar' && (
                <MainPanelHeader
                  className={css.mainPanel}
                  sortByComponent={sortBy('desktop')}
                  isSortByActive={sortConfig.active}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  noResultsInfo={noResultsInfo}
                />
              )}
              {shopFilters.filterType === 'header' && (
                <MainPanelHeaderWithFilters
                  className={css.mainPanel}
                  sortByComponent={sortBy('desktop')}
                  isSortByActive={sortConfig.active}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  noResultsInfo={noResultsInfo}
                  availableFilters={availableFilters}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={urlQueryParams}
                  initialValues={initialValues(this.props, this.state.currentQueryParams)}
                  getHandleChangedValueFn={this.getHandleChangedValueFn}
                  intl={intl}
                />
              )}
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  conversionRates={conversionRates}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    conversionRates,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    conversionRates,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
